import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ChartBarIcon,
  CogIcon,
  DocumentDuplicateIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import AnalyticsDashboard from './AnalyticsDashboard';
import { DebugLabel } from './shared';
import { useAuth } from '../contexts/AuthContext';
import { fetchDashboardStats, type DashboardStats } from '../lib/api/dashboard';
import { UsageMeters } from './dashboard/UsageMeters';
import { entitlementService, type EntitlementStatus } from '../services/entitlement';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';

interface DashboardProps {
  user?: {
    id: string;
    email: string;
    displayName: string;
    subscriptionTier: 'free' | 'pro' | 'team' | 'enterprise';
  };
}

type DashboardTabId = 'overview' | 'analytics';

type NavigationItem = {
  id: DashboardTabId;
  name: string;
  icon: typeof HomeIcon;
  description: string;
  proOnly?: boolean;
};

export const UserDashboard: React.FC<DashboardProps> = ({ user }) => {
  const navigate = useNavigate();
  const { user: currentUser, dbProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTabId>('overview');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const paywallEnabled = useFeatureFlag('PAYWALL');

  // Fetch real dashboard data from API
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
        setError('Failed to load dashboard statistics');
        // Set default values on error
        setStats({
          totalComparisons: 0,
          monthlyTokenUsage: 0,
          monthlyTokenLimit: 100000,
          monthlyTokenCostUsd: 0,
          avgScore: null,
          favoriteModel: null,
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Fetch entitlements for usage meters
  useEffect(() => {
    if (!paywallEnabled) {
      console.log('[UserDashboard] PAYWALL disabled, using unlimited defaults');
      const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
      setEntitlementStatus(unlimitedDefaults);
      return;
    }

    const fetchEntitlements = async () => {
      try {
        console.log('[UserDashboard] Fetching entitlement status...');
        const status = await entitlementService.getStatus();
        console.log('[UserDashboard] Entitlement status received:', {
          plan: status.plan?.code,
          userTimezone: status.userTimezone,
          fullStatus: status
        });
        setEntitlementStatus(status);
      } catch (err) {
        console.error('[UserDashboard] Failed to fetch entitlements:', err);
        const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
        setEntitlementStatus(unlimitedDefaults);
      }
    };
    fetchEntitlements();
  }, [paywallEnabled]);

  const navigation: NavigationItem[] = [
    {
      id: 'overview',
      name: 'Overview',
      icon: HomeIcon,
      description: 'Dashboard summary and recent activity'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: ChartBarIcon,
      description: 'Detailed usage insights and trends',
      proOnly: user?.subscriptionTier === 'free'
    }
  ];

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'pro': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'team': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'enterprise': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // SUPPRESSED: Token quota - Analytics only, not enforced
  // const tokenQuota = entitlementStatus?.quotas?.tokens || (stats ? {
  //   used: stats.monthlyTokenUsage,
  //   limit: stats.monthlyTokenLimit,
  //   remaining: Math.max(0, stats.monthlyTokenLimit - stats.monthlyTokenUsage),
  //   percentage: stats.monthlyTokenLimit > 0
  //     ? (stats.monthlyTokenUsage / stats.monthlyTokenLimit) * 100
  //     : 0,
  //   allowed: stats.monthlyTokenUsage < stats.monthlyTokenLimit,
  // } : undefined);

  return (
    <div className="min-h-screen bg-transparent">
      <DebugLabel label="UserDashboard-Container" type="component" position="top-left" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 pb-24 md:pb-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar Navigation */}
          <nav className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4 sm:p-5 lg:p-6 lg:sticky lg:top-8">
              <div className="space-y-3">
              {navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  disabled={item.proOnly}
                  className={`w-full flex items-center space-x-3 p-3 sm:p-4 text-sm sm:text-base rounded-xl transition-all duration-200 text-left group ${
                      activeTab === item.id
                        ? 'bg-cyan-500/20 border border-cyan-400/30 text-cyan-300'
                        : item.proOnly
                        ? 'bg-slate-800/50 border border-slate-700/30 text-slate-500 cursor-not-allowed'
                        : 'bg-slate-800/50 border border-slate-700/30 text-slate-300 hover:bg-slate-700/50 hover:border-slate-600/50'
                    }`}
                  >
                    <item.icon className={`h-5 w-5 ${item.proOnly ? 'text-slate-500' : ''}`} />
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {item.name}
                        {item.proOnly && (
                          <StarIcon className="h-4 w-4 text-amber-400" />
                        )}
                      </div>
                      <div className="text-xs opacity-75 mt-1">
                        {item.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 space-y-6 md:space-y-8">
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Usage Meters - Daily and Total Comparisons */}
                  {entitlementStatus && (
                    <div className="mb-6 md:mb-8">
                      <UsageMeters
                        dailyQuota={entitlementStatus.quotas.comparisons}
                        savedQuota={entitlementStatus.quotas.savedComparisons}
                        planName={entitlementStatus.plan.displayName}
                        userTimezone={entitlementStatus.userTimezone || dbProfile?.timezone || 'UTC'}
                        paywallEnabled={paywallEnabled}
                      />
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'analytics' && (
                <motion.div
                  key="analytics"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {user?.subscriptionTier === 'free' ? (
                    <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 text-center">
                      <div className="mb-6">
                        <ChartBarIcon className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">Analytics Dashboard</h3>
                        <p className="text-slate-400">
                          Upgrade to Pro to access detailed analytics, usage insights, and performance metrics.
                        </p>
                      </div>
                      <div className="flex items-center justify-center space-x-4">
                        <button className="px-6 py-3 bg-slate-700/50 border border-slate-600/50 text-slate-300 rounded-lg hover:bg-slate-600/50 transition-colors">
                          Learn More
                        </button>
                      </div>
                    </div>
                  ) : (
                    <AnalyticsDashboard
                      avgScore={stats?.avgScore}
                      favoriteModel={stats?.favoriteModel}
                    />
                  )}
                </motion.div>
              )}

            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
